import type { TDashboardLayoutSource } from "@/types/(company)/dashboardLayout.interface";
import {
  moveItem,
  normalizeSize,
  placementsEqual,
  sanitizePlacements,
} from "./grid.utils";
import type {
  IWidgetDefinition,
  IWidgetPlacement,
  TWidgetSizeKey,
} from "./types";

/**
 * Everything the canvas knows about its own contents.
 *
 * `placements` is null until the saved layout has been fetched — a distinct
 * "not known yet" that an empty array cannot express, and the difference matters
 * because an empty canvas is a legitimate saved state.
 */
export interface ILayoutState {
  placements: IWidgetPlacement[] | null;
  /**
   * The arrangement the server state currently produces — the saved row when
   * there is one, the registry default when there is not. `null` only ever means
   * "not hydrated yet".
   *
   * Seeding it with the default rather than leaving it null is what stops every
   * first page load from writing a row: on arrival there is genuinely nothing to
   * save, and the diff against this is the whole of "is there anything to send".
   */
  saved: IWidgetPlacement[] | null;
  /**
   * The widget added most recently. The canvas pulses a ring on it once so the
   * eye is taken to what just appeared instead of the user scanning the grid for
   * it — especially while the library is still open over the canvas.
   */
  arrivingId: string | null;
  /**
   * Whether this arrangement is the person's own or the shipped default.
   *
   * Distinct from `saved`, which only says "does what is on screen match the
   * server". Both are false-y on a fresh load, but they answer different
   * questions: `saved` drives whether to write, this drives what to tell the
   * reader. Without it the two states are indistinguishable on screen — the
   * canvas looks identical whether you arranged it or never touched it.
   */
  isCustomized: boolean;
  /** Mirrors `isCustomized` for the command bar's provenance chip. */
  source: TDashboardLayoutSource;
}

export type TLayoutAction =
  /** First read of the server layout (or the default when there is none). */
  | {
      type: "hydrate";
      placements: IWidgetPlacement[];
      isCustomized: boolean;
      source: TDashboardLayoutSource;
    }
  /** The placement is built by the caller — ids are random, and a reducer must be pure. */
  | { type: "add"; placement: IWidgetPlacement }
  | { type: "remove"; instanceId: string }
  /** Undo of a remove: puts the original placement back at its original index. */
  | { type: "restore"; placement: IWidgetPlacement; index: number }
  | { type: "resize"; instanceId: string; size: TWidgetSizeKey }
  | { type: "move"; activeId: string; overId: string }
  /** Wholesale swap that leaves the canvas dirty — used by undo-of-reset. */
  | { type: "replace"; placements: IWidgetPlacement[] }
  /**
   * Back to the default, and clean.
   *
   * Sets `placements` and `saved` in one transition on purpose. Doing it as a
   * `replace` and then a `markSaved` after the DELETE resolves leaves the canvas
   * dirty in between, which is long enough for the save debounce to fire and PUT
   * the default back — re-creating the row the reset just deleted.
   */
  | {
      type: "reset";
      placements: IWidgetPlacement[];
      source: TDashboardLayoutSource;
    }
  /** The server accepted this exact arrangement. */
  | { type: "markSaved"; placements: IWidgetPlacement[] | null }
  | { type: "clearArrival" };

export const INITIAL_LAYOUT_STATE: ILayoutState = {
  placements: null,
  saved: null,
  arrivingId: null,
  isCustomized: false,
  source: "fallback",
};

/**
 * The canvas state machine.
 *
 * Curried over the registry because three of the transitions need it (singleton
 * enforcement, size clamping, dropping retired widget types) and a reducer that
 * reached for it from a closure over component scope would not be pure. Pure is
 * the point: every transition here is a plain function of state and action, so
 * the whole of "what the canvas does" can be read — and tested — without a
 * React renderer, a network layer or a permissions fixture.
 *
 * Identity is preserved wherever a placement did not change: `resize` re-creates
 * only the placement it touched, `move` re-orders without re-creating any. That
 * is what makes `React.memo` on the widget frames actually block re-renders
 * rather than merely delay a shallow compare.
 */
export function createLayoutReducer(
  definitions: Map<string, IWidgetDefinition>,
) {
  return function layoutReducer(
    state: ILayoutState,
    action: TLayoutAction,
  ): ILayoutState {
    switch (action.type) {
      case "hydrate": {
        const hydrated = sanitizePlacements(action.placements, definitions);
        // Both fields, same value: what is on screen is exactly what the server
        // state produces, so the canvas starts clean.
        return {
          placements: hydrated,
          saved: hydrated,
          arrivingId: null,
          isCustomized: action.isCustomized,
          source: action.source,
        };
      }

      case "add": {
        if (!state.placements) return state;

        const definition = definitions.get(action.placement.type);
        if (!definition) return state;

        const isDuplicateSingleton =
          Boolean(definition.singleton) &&
          state.placements.some(
            (placement) => placement.type === action.placement.type,
          );
        if (isDuplicateSingleton) return state;

        return {
          ...state,
          placements: [
            ...state.placements,
            {
              ...action.placement,
              size: normalizeSize(definition, action.placement.size),
            },
          ],
          arrivingId: action.placement.instanceId,
        };
      }

      case "remove": {
        if (!state.placements) return state;
        const next = state.placements.filter(
          (placement) => placement.instanceId !== action.instanceId,
        );
        if (next.length === state.placements.length) return state;
        return { ...state, placements: next };
      }

      case "restore": {
        if (!state.placements) return state;
        const alreadyBack = state.placements.some(
          (placement) => placement.instanceId === action.placement.instanceId,
        );
        if (alreadyBack) return state;

        const next = state.placements.slice();
        next.splice(Math.min(action.index, next.length), 0, action.placement);
        return { ...state, placements: next };
      }

      case "resize": {
        if (!state.placements) return state;

        let changed = false;
        const next = state.placements.map((placement) => {
          if (placement.instanceId !== action.instanceId) return placement;

          const definition = definitions.get(placement.type);
          if (!definition) return placement;

          const size = normalizeSize(definition, action.size);
          if (size === placement.size) return placement;

          changed = true;
          return { ...placement, size };
        });

        return changed ? { ...state, placements: next } : state;
      }

      case "move": {
        if (!state.placements) return state;
        const from = state.placements.findIndex(
          (placement) => placement.instanceId === action.activeId,
        );
        const to = state.placements.findIndex(
          (placement) => placement.instanceId === action.overId,
        );
        if (from === -1 || to === -1 || from === to) return state;
        return { ...state, placements: moveItem(state.placements, from, to) };
      }

      case "replace": {
        const next = sanitizePlacements(action.placements, definitions);
        if (state.placements && placementsEqual(next, state.placements)) {
          return state;
        }
        return { ...state, placements: next };
      }

      case "reset": {
        const next = sanitizePlacements(action.placements, definitions);
        return {
          placements: next,
          saved: next,
          arrivingId: null,
          isCustomized: false,
          source: action.source,
        };
      }

      case "markSaved":
        // A successful write is the moment an arrangement becomes the person's
        // own — before it lands they are still on whatever the server had.
        return {
          ...state,
          saved: action.placements,
          isCustomized: true,
          source: "user",
        };

      case "clearArrival":
        return state.arrivingId === null
          ? state
          : { ...state, arrivingId: null };

      default:
        return state;
    }
  };
}

/**
 * Is there an edit the server has not been told about yet?
 *
 * False before hydration and false at rest, so the persistence effect below can
 * key straight off it without needing its own "have we loaded" guard.
 */
export function isLayoutDirty(state: ILayoutState): boolean {
  if (!state.placements || !state.saved) return false;
  return !placementsEqual(state.placements, state.saved);
}
